import { createStore, select, withProps } from '@ngneat/elf';
import { BehaviorSubject, distinctUntilChanged, Observable, of } from 'rxjs';

import { SecondaryNavKey } from '../models/secondary-nav-key';
import { SecondaryNavOption } from '../models/secondary-nav-option';

const secondaryNavOptionsSetSubject = new BehaviorSubject(false);

interface SecondaryNavProps {
  optionsLookup: Map<SecondaryNavKey, SecondaryNavOption[]>;
}

const secondaryNavStore = createStore(
  { name: 'secondary-nav' },
  withProps<SecondaryNavProps>({
    optionsLookup: new Map(),
  })
);

export const observeSecondaryNavOptions = (function () {
  const memoizedPipes = new Map<
    SecondaryNavKey,
    Observable<SecondaryNavOption[]>
  >();

  return function (key: SecondaryNavKey) {
    if (!memoizedPipes.has(key)) {
      memoizedPipes.set(
        key,
        secondaryNavStore.pipe(
          select(({ optionsLookup }) => optionsLookup.get(key) ?? [])
        )
      );
    }

    return memoizedPipes.get(key) ?? of([]);
  };
})();

export function getSecondaryNavOptionsSnapshot(key: SecondaryNavKey) {
  return secondaryNavStore.getValue().optionsLookup.get(key) ?? [];
}

export const secondaryNavOptionsSet$ = secondaryNavOptionsSetSubject
  .asObservable()
  .pipe(distinctUntilChanged());

let cfsRedirectUrl: null | string = null;

export function getCfsRedirectUrl() {
  return cfsRedirectUrl;
}

export function setCfsRedirectUrl(value: string) {
  cfsRedirectUrl = value;
}

export function clearCfsRedirectUrl() {
  cfsRedirectUrl = null;
}

let manageAccountRedirectUrl: null | string = null;

export function getManageAccountRedirectUrl() {
  return manageAccountRedirectUrl;
}

export function setManageAccountRedirectUrl(value: string) {
  manageAccountRedirectUrl = value;
}

export function clearManageAccountRedirectUrl() {
  manageAccountRedirectUrl = null;
}

let reportingRedirectUrl: null | string = null;

export function getReportingRedirectUrl() {
  return reportingRedirectUrl;
}

export function setReportingRedirectUrl(value: string) {
  reportingRedirectUrl = value;
}

export function clearReportingRedirectUrl() {
  reportingRedirectUrl = null;
}

let diagnosticsRedirectUrl: null | string = null;

export function getDiagnosticsRedirectUrl() {
  return diagnosticsRedirectUrl;
}

export function setDiagnosticsRedirectUrl(value: string) {
  diagnosticsRedirectUrl = value;
}

export function clearDiagnosticsRedirectUrl() {
  diagnosticsRedirectUrl = null;
}

export function updateSecondaryNavOptions(
  key: SecondaryNavKey,
  options: SecondaryNavOption[]
) {
  secondaryNavStore.update((state) => {
    const optionsLookup = new Map<SecondaryNavKey, SecondaryNavOption[]>(
      state.optionsLookup
    );
    optionsLookup.set(key, options);

    return {
      ...state,
      optionsLookup,
    };
  });
  secondaryNavOptionsSetSubject.next(true);
}

export function resetSecondaryNavStore() {
  secondaryNavStore.reset();
}
